import React, { useState, useEffect, useMemo } from "react";
import { Spin, Empty, Button } from "antd";
import { TpmBar, TpmPie } from "../../home/chartKit";
import { HOME_COLORS, HOME_SERIES } from "../../home/homeTheme";
import "./TpmSummary.scss";
import iconCal from "../../../../Assets/Images/iconCalendar.png";
import iconCol1 from "../../../../Assets/Images/iconCollection1.png";
import iconCol2 from "../../../../Assets/Images/iconCollection2.png";
import snapshot1 from "../../../../Assets/svg/snapshot1.svg";
import snapshot2 from "../../../../Assets/svg/snapshot2.svg";
import snapshot3 from "../../../../Assets/svg/snapshot3.svg";
import snapshot4 from "../../../../Assets/svg/snapshot4.svg";
import snapshot5 from "../../../../Assets/svg/snapshot5.svg";
import snapshot6 from "../../../../Assets/svg/snapshot6.svg";
import location1 from "../../../../Assets/svg/location1.svg";
import location2 from "../../../../Assets/svg/location2.svg";
import location3 from "../../../../Assets/svg/location3.svg";
import location4 from "../../../../Assets/svg/location4.svg";
import location5 from "../../../../Assets/svg/location5.svg";
import location6 from "../../../../Assets/svg/location6.svg";
import ExportButton from "../../../../Assets/svg/exportButton.svg";
import warningIcon from "../../../../Assets/svg/warning.svg";
import { httpService } from "../../../../services/httpService.service";
import { months } from "../../../../services/Enums/monthsAndYears";
import ExportModal from "./ExportModal";
import { isAuthorized } from "../../../../services/authService";
import { RolesEnum } from "../../../../services/rolesEnum";
import MapComponent from "../../../components/MapComponent/MapComponent";
import { DownOutlined, UpOutlined } from "@ant-design/icons";
import ContractRangeSelectors from "../../../components/ContractRamgeSelectors/ContractRangeSelectors";
import DynamicChart from "../../../components/charts/DynamicChart";
import { formatChartData } from "../../../components/charts/formatChartData";
import FlagPatternsPanel from "../../../components/FlagPatterns/FlagPatternsPanel";

const createBarData = (data, colors) => {
  return {
    labels: data.map((item) => item.type),
    datasets: [
      {
        label: "Count",
        data: data.map((item) => item.value ?? 0),
        backgroundColor: colors.slice(0, data.length),
        borderWidth: 1,
      },
    ],
  };
};

const horizontalBarOptions = {
  indexAxis: "y",
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: { enabled: true },
  },
  scales: {
    x: { beginAtZero: true },
    y: { ticks: { font: { size: 10 } } },
  },
};

function TpmSummary() {
  const [sampleSizeCards, setSampleSizeCards] = useState({
    data: [],
    toShow: 6,
  });
  const [collapse, setCollapse] = useState({
    activityByLocation: false,
    activitySnapshot: false,
    ipDonorReporting: false,
    // Escalation signals open by default — the point of F-03 is that the PM
    // sees stalled work without going looking for it.
    stalledSignals: true,
  });
  const isGuestShare = process.env.REACT_APP_API_IS_GUEST;
  const [projectData, setProjectData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showGraphs, setShowGraphs] = useState(true);
  // One muted series for every chart on this tab (see homeTheme) instead of
  // two hand-rolled rainbows.
  const siteVisitsColors = HOME_SERIES;
  const redFlagsColors = HOME_SERIES;
  const projectRoundName = localStorage.getItem("projectRoundName");
  const [range, setRange] = useState({
    year: undefined,
    month: undefined,
    quarter: undefined,
  });

  useEffect(() => {
    const handleResize = () => {
      setShowGraphs(window.innerWidth >= 500);
    };

    handleResize();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const fetchProjectData = async () => {
      setLoading(true);
      try {
        const projectID = localStorage.getItem("currentProjectID");
        if (projectID) {
          let apiProject;
          // Construct the API URL based on selected filters
          if (range.year && range.month) {
            // If year and month are selected
            apiProject = `/api/Projects/getTPMsummary?projectId=${projectID}&year=${range.year}&month=${range.month}`;
          } else if (range.year && range.quarter) {
            // If year and quarter are selected
            apiProject = `/api/Projects/getTPMsummary?projectId=${projectID}&year=${range.year}&Quarter=${range.quarter}`;
          } else if (range.year) {
            // If only year is selected
            apiProject = `/api/Projects/getTPMsummary?projectId=${projectID}&year=${range.year}`;
          } else {
            // If no filters selected, just project ID
            apiProject = `/api/Projects/getTPMsummary?projectId=${projectID}`;
          }

          httpService.get(
            apiProject,
            (data) => {
              setProjectData(data);
              setSampleSizeCards((prev) => ({
                ...prev,
                data: data?.data?.activityByLocation
                  ?.costumPropertiesCalculated,
              }));
              setLoading(false);
            },
            (error) => {
              console.error("Error fetching project data:", error);
              setLoading(false);
            }
          );
        }
      } catch (error) {
        console.error("Error fetching project data:", error);
        setLoading(false);
      }
    };

    fetchProjectData();
  }, [range.year, range.month, range.quarter]);

  // if (loading) {
  //   return <div className="w-100 p-5 justify-content-center"><Spin /></div>;
  // }

  const createPieData = (data, colors) => {
    const noData = data.every((item) => item.value === 0);

    if (noData) return null;

    return {
      labels: data?.map((item) => item.type),
      datasets: [
        {
          data: data?.map((item) => item.value),
          backgroundColor: colors,
          hoverBackgroundColor: colors,
        },
      ],
    };
  };

  const pieOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      tooltip: {
        enabled: true,
      },
    },
    cutout: "70%",
  };

  const siteVisitTrackingData = [
    // {
    //   type: "Data Check",
    //   value: projectData?.data?.siteVisitTracking?.dataCheck,
    // },
    // {
    //   type: "Data Collection",
    //   value: projectData?.data?.siteVisitTracking?.dataCollection,
    // },
    // {
    //   type: "Translation",
    //   value: projectData?.data?.siteVisitTracking?.translation,
    // },
    {
      type: "Report Writing",
      value: projectData?.data?.siteVisitTracking?.reportWriting,
    },
    // {
    //   type: "Reporting Completed",
    //   value: projectData?.data?.siteVisitTracking?.reportingCompleted,
    // },
    {
      type: "Report Submitted",
      value: projectData?.data?.siteVisitTracking?.reportSubmitted,
    },
    {
      type: "Report Approved",
      value: projectData?.data?.siteVisitTracking?.reportApproved,
    },
  ];

  const siteVisitsData = [
    {
      type: "Data Check",
      value: projectData?.data?.siteVisitsProgress?.dataCheck,
    },
    {
      type: "Data Collection",
      value: projectData?.data?.siteVisitsProgress?.dataCollection,
    },
    {
      type: "Planning",
      value: projectData?.data?.siteVisitsProgress?.planning,
    },
    {
      type: "Pre-Planning Phase",
      value: projectData?.data?.siteVisitsProgress?.prePlanningPhase,
    },
    {
      type: "Visit Completed",
      value: projectData?.data?.siteVisitsProgress?.visitCompleted,
    },
  ];

  const redFlagsData = [
    {
      type: "Archived",
      value: projectData?.data?.redFlagsProgress?.archived,
    },
    {
      type: "Not Addressed",
      value: projectData?.data?.redFlagsProgress?.assigned,
    },
    {
      type: "In Progress",
      value: projectData?.data?.redFlagsProgress?.inprogress,
    },
    {
      type: "On Hold",
      value: projectData?.data?.redFlagsProgress?.onhold,
    },
    {
      type: "Resolved",
      value: projectData?.data?.redFlagsProgress?.resolved,
    },
  ];

  const sampleSizeChartData = {
    labels: sampleSizeCards?.data?.map((item) => item?.name || ""),
    datasets: [
      {
        axis: "y",
        label: "",
        data: sampleSizeCards?.data?.map((item) => item?.count || 0),
        fill: false,
        backgroundColor: HOME_COLORS.brand,
        borderColor: HOME_COLORS.brand,
        barThickness: 8,
      },
    ],
  };

  const sampleSizeBarOptions = {
    responsive: true,
    // The wrapper below sizes itself from the number of bars, so the canvas
    // fills it rather than holding a ratio.
    maintainAspectRatio: false,
    indexAxis: "y",
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const value = context.raw;
            return `Count: ${value}`;
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
      },
      y: {
        stacked: true,
        categoryPercentage: 0.9,
        barPercentage: 0.9,
      },
    },
  };

  const handleRangeChange = (newValue) => {
    setRange(newValue);
  };

  // The whole section header is the toggle, so the target is the title row
  // rather than a chevron at the far right of it.
  const toggleSection = (key) =>
    setCollapse((prev) => ({ ...prev, [key]: !prev[key] }));

  // The selected period, as one chip beside the round name.
  const periodLabel = [
    range.quarter ? `Quarter ${range.quarter}` : null,
    range.month ? months[range.month] : null,
    range.year ? `${range.year}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  // F-03 — stalled counts. `stalledOverview` is the dedicated block; the tasks
  // overview also carries the task count, kept as a fallback so the KPI still
  // reads if only one of the two is populated.
  const stalledOverview = useMemo(() => {
    const overview = projectData?.data?.stalledOverview || {};
    const visits = overview.stalledSiteVisits || 0;
    const tasks =
      overview.stalledTasks ??
      projectData?.data?.tasksOverviewResponse?.summary?.stalled ??
      0;
    return { visits, tasks, total: visits + tasks };
  }, [projectData]);

  return (
    <Spin spinning={loading}>
      <div className="tpmSummaryDiv">
        {/* One line instead of a 68px magenta band plus a control stack that had
            to be dragged back up with a negative margin: what period is on
            screen on the left, what changes it on the right. */}
        <div className="tpmTopBar">
          <span className="tpmTopBarTitle">
            <span className="tpmTopBarIcon">
              <img src={iconCal} alt="" />
            </span>
            {projectRoundName}
          </span>
          {periodLabel && (
            <span className="tpmTopBarPeriod">{periodLabel}</span>
          )}
          <div className="tpmTopBarControls">
            <ContractRangeSelectors
              enableQuarter={true}
              value={range}
              onChange={handleRangeChange}
            />
            {isAuthorized([
              RolesEnum.SystemAdmin,
              RolesEnum.ProjectManager,
              RolesEnum.FieldCoordinator,
              RolesEnum.FieldTeamLeader,
            ]) && (
              <Button
                className="addButtonTPM"
                onClick={() => setShowExportModal(true)}
              >
                <img src={ExportButton} alt="ExportButton" />
                Export Dashboard
              </Button>
            )}
            <ExportModal
              visible={showExportModal}
              onCancel={() => {
                setShowExportModal(false);
              }}
              data={projectData}
            />
          </div>
        </div>
        {/* Two equal columns rather than two charts stacked beside one: the
            three progress charts are the same kind of thing, so they read as a
            set when they are the same size. */}
        {showGraphs && (
          <div className="tpmChartGrid">
            <DynamicChart
              title="Site Visits Progress"
              icon={<img src={iconCol1} alt="img" />}
              loading={loading}
              data={formatChartData(siteVisitsData, siteVisitsColors)}
              colors={siteVisitsColors}
              defaultChartType="bar"
            />
            <DynamicChart
              title="Site Visit Tracking Progress"
              icon={<img src={iconCol1} alt="img" />}
              loading={loading}
              data={formatChartData(siteVisitTrackingData, siteVisitsColors)}
              colors={siteVisitsColors}
              defaultChartType="bar"
            />
            {!isGuestShare && (
              <DynamicChart
                title="Red Flags Progress"
                icon={<img src={iconCol2} alt="img" />}
                loading={loading}
                data={formatChartData(redFlagsData, redFlagsColors)}
                colors={redFlagsColors}
              />
            )}
          </div>
        )}
        {/* F-03 / Flags Intelligence — escalation signals. Both come straight
            off the summary response: stalledOverview counts and flagPatterns
            (active concentrations, independent of the month/quarter/year
            filter above). Same section shell, KPI cards and collapse control as
            every other block on this dashboard. */}
        {!isGuestShare && (
          <div className="activitySnapshot">
            <div
              className="activitySnapshotHead"
              role="button"
              tabIndex={0}
              onClick={() => toggleSection("stalledSignals")}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") toggleSection("stalledSignals");
              }}
            >
              <h2 className="activitySnapshotHeader">
                Stalled Items &amp; Flag Patterns
              </h2>
              <span className="activitySnapshotToggle">
                {collapse.stalledSignals ? <UpOutlined /> : <DownOutlined />}
              </span>
            </div>
            {collapse.stalledSignals && (
              <>
                <div className="activitySnapshotResultCard">
                  <div className="activitySnapshotCard">
                    <div className="activitySnapshotNumber">
                      <h2 className="activitySnapshotNumber1">
                        {stalledOverview.total}
                      </h2>
                      <p className="activitySnapshotText">Stalled Items</p>
                    </div>
                    <div className="activitySnapshotImg">
                      <img src={warningIcon} alt="img" />
                    </div>
                  </div>
                  <div className="activitySnapshotCard">
                    <div className="activitySnapshotNumber">
                      <h2 className="activitySnapshotNumber1">
                        {stalledOverview.visits}
                      </h2>
                      <p className="activitySnapshotText">
                        Stalled Site Visits
                      </p>
                    </div>
                    <div className="activitySnapshotImg">
                      <img src={snapshot5} alt="img" />
                    </div>
                  </div>
                  <div className="activitySnapshotCard">
                    <div className="activitySnapshotNumber">
                      <h2 className="activitySnapshotNumber1">
                        {stalledOverview.tasks}
                      </h2>
                      <p className="activitySnapshotText">Stalled Tasks</p>
                    </div>
                    <div className="activitySnapshotImg">
                      <img src={snapshot1} alt="img" />
                    </div>
                  </div>
                </div>
                {/* The section header above already names this block, so the
                    pattern list renders bare inside it. */}
                <FlagPatternsPanel
                  patterns={projectData?.data?.flagPatterns}
                  loading={loading && !projectData}
                  bare
                />
              </>
            )}
          </div>
        )}
        {!isGuestShare && (
          <div className="activitySnapshot">
            <div
              className="activitySnapshotHead"
              role="button"
              tabIndex={0}
              onClick={() => toggleSection("activitySnapshot")}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") toggleSection("activitySnapshot");
              }}
            >
              <h2 className="activitySnapshotHeader">Activity Snapshot</h2>
              <span className="activitySnapshotToggle">
                {collapse.activitySnapshot ? <UpOutlined /> : <DownOutlined />}
              </span>
            </div>
            {collapse.activitySnapshot && (
              <div className="activitySnapshotResultCard">
                <div className="activitySnapshotCard">
                  <div className="activitySnapshotNumber">
                    <h2 className="activitySnapshotNumber1">
                      {
                        projectData?.data?.activitySnapshot
                          ?.activeIpPlanningCards
                      }
                    </h2>
                    <p className="activitySnapshotText">
                      Active IP Planning Cards
                    </p>
                  </div>
                  <div className="activitySnapshotImg">
                    <img src={snapshot1} alt="img" />
                  </div>
                </div>
                <div className="activitySnapshotCard">
                  <div className="activitySnapshotNumber">
                    <h2 className="activitySnapshotNumber1">
                      {projectData?.data?.activitySnapshot?.activeRedFlagCards}
                    </h2>
                    <p className="activitySnapshotText">
                      Active Red Flag Cards
                    </p>
                  </div>
                  <div className="activitySnapshotImg">
                    <img src={snapshot2} alt="img" />
                  </div>
                </div>
                <div className="activitySnapshotCard">
                  <div className="activitySnapshotNumber">
                    <h2 className="activitySnapshotNumber1">
                      {projectData?.data?.activitySnapshot?.projectMembers}
                    </h2>
                    <p className="activitySnapshotText">Project Members</p>
                  </div>
                  <div className="activitySnapshotImg">
                    <img src={snapshot3} alt="img" />
                  </div>
                </div>
                <div className="activitySnapshotCard">
                  <div className="activitySnapshotNumber">
                    <h2 className="activitySnapshotNumber1">
                      {
                        projectData?.data?.activitySnapshot
                          ?.activeEnumeratorTeamCards
                      }
                    </h2>
                    <p className="activitySnapshotText">
                      Active Enumerator Team Cards
                      <br />
                    </p>
                  </div>
                  <div className="activitySnapshotImg">
                    <img src={snapshot4} alt="img" />
                  </div>
                </div>
                <div className="activitySnapshotCard">
                  <div className="activitySnapshotNumber">
                    <h2 className="activitySnapshotNumber1">
                      {
                        projectData?.data?.activitySnapshot
                          ?.activeSiteVisitCards
                      }
                    </h2>
                    <p className="activitySnapshotText">
                      Active Site Visit Cards
                    </p>
                  </div>
                  <div className="activitySnapshotImg">
                    <img src={snapshot5} alt="img" />
                  </div>
                </div>
                <div className="activitySnapshotCard">
                  <div className="activitySnapshotNumber">
                    <h2 className="activitySnapshotNumber1">
                      {projectData?.data?.activitySnapshot?.activeTripCards}
                    </h2>
                    <p className="activitySnapshotText">Active Trip Cards</p>
                  </div>
                  <div className="activitySnapshotImg">
                    <img src={snapshot6} alt="img" />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        <div className="activitySnapshot">
          <div
            className="activitySnapshotHead"
            role="button"
            tabIndex={0}
            onClick={() => toggleSection("sampleSizeCards")}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") toggleSection("sampleSizeCards");
            }}
          >
            <h2 className="activitySnapshotHeader">Sample Size Tools</h2>
            <span className="activitySnapshotToggle">
              {collapse.sampleSizeCards ? <UpOutlined /> : <DownOutlined />}
            </span>
          </div>
          {collapse.sampleSizeCards && (
            // One horizontal bar per sample-size card, so the box grows with the
            // number of rows instead of squeezing them into a fixed canvas.
            <div
              className="activitySnapshotChart"
              style={{
                height: Math.max(
                  180,
                  (sampleSizeCards?.data?.length || 0) * 26 + 40
                ),
              }}
            >
              <TpmBar
                data={sampleSizeChartData}
                options={sampleSizeBarOptions}
              />
            </div>
          )}
        </div>
        <div className="activitySnapshot">
          <div
            className="activitySnapshotHead"
            role="button"
            tabIndex={0}
            onClick={() => toggleSection("activityByLocation")}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") toggleSection("activityByLocation");
            }}
          >
            <h2 className="activitySnapshotHeader">Activity by Location</h2>
            <span className="activitySnapshotToggle">
              {collapse.activityByLocation ? <UpOutlined /> : <DownOutlined />}
            </span>
          </div>
          {collapse.activityByLocation && (
            <>
              <div className="activitySnapshotResultCard">
                <div className="activitySnapshotCard1">
                  <div className="activitySnapshotNumber">
                    <h2 className="activitySnapshotNumber1">
                      {projectData?.data?.activityByLocation?.plannedVisits}
                    </h2>
                    <p className="activitySnapshotText">Target Visits</p>
                  </div>
                  <div className="activitySnapshotImg1">
                    <img src={location1} alt="img"></img>
                  </div>
                </div>
                {!isGuestShare && (
                  <div className="activitySnapshotCard1">
                    <div className="activitySnapshotNumber">
                      <h2 className="activitySnapshotNumber1">
                        {projectData?.data?.activityByLocation?.postponedCards}
                      </h2>
                      <p className="activitySnapshotText">Postponed Cards</p>
                    </div>
                    <div className="activitySnapshotImg1">
                      <img src={location2} alt="img"></img>
                    </div>
                  </div>
                )}
                <div className="activitySnapshotCard1">
                  <div className="activitySnapshotNumber">
                    <h2 className="activitySnapshotNumber1">
                      {projectData?.data?.activityByLocation?.completedVisits}
                    </h2>
                    <p className="activitySnapshotText">Completed Visits</p>
                  </div>
                  <div className="activitySnapshotImg1">
                    <img src={location3} alt="img"></img>
                  </div>
                </div>
                <div className="activitySnapshotCard1">
                  <div className="activitySnapshotNumber">
                    <h2 className="activitySnapshotNumber1">
                      {
                        projectData?.data?.activityByLocation
                          ?.completedBeneficiaryInterviews
                      }{" "}
                      <span>completed</span>
                    </h2>
                    <p className="activitySnapshotText">
                      Beneficiary Interviews
                    </p>
                  </div>
                  <div className="activitySnapshotImg1">
                    <img src={location4} alt="img"></img>
                  </div>
                </div>
                <div className="activitySnapshotCard1">
                  <div className="activitySnapshotNumber">
                    <h2 className="activitySnapshotNumber1">
                      {
                        projectData?.data?.activityByLocation
                          ?.completedClientsInterviews
                      }
                      <span>completed</span>
                    </h2>
                    <p className="activitySnapshotText">Clients Interviews</p>
                  </div>
                  <div className="activitySnapshotImg1">
                    <img src={location5} alt="img"></img>
                  </div>
                </div>
                <div className="activitySnapshotCard1">
                  <div className="activitySnapshotNumber">
                    <h2 className="activitySnapshotNumber1">
                      {
                        projectData?.data?.activityByLocation
                          ?.completedObservationInterviews
                      }
                      <span>completed</span>
                    </h2>
                    <p className="activitySnapshotText">
                      Observation Checklist
                    </p>
                  </div>

                  <div className="activitySnapshotImg1">
                    <img src={location6} alt="img"></img>
                  </div>
                </div>
              </div>
              <MapComponent availableStatuses={[2, 3, 4, 5, 6]}></MapComponent>
            </>
          )}
        </div>
        {!isGuestShare && (
          <>
            <div className="activitySnapshot">
              <div
                className="activitySnapshotHead"
                role="button"
                tabIndex={0}
                onClick={() => toggleSection("ipDonorReporting")}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") toggleSection("ipDonorReporting");
                }}
              >
                <h2 className="activitySnapshotHeader">
                  IP/Donor Reporting Status Snapshot
                </h2>
                <span className="activitySnapshotToggle">
                  {collapse.ipDonorReporting ? <UpOutlined /> : <DownOutlined />}
                </span>
              </div>
              {collapse.ipDonorReporting && (
                <div className="donorsDiv">
                  <div className="donorsLeft">
                    <div className="donorsCardsContainer">
                      <div className="donorsCard">
                        {" "}
                        <div className="activitySnapshotNumber">
                          <h2 className="activitySnapshotNumber1">
                            {" "}
                            {
                              projectData?.data?.ipDonorReporting
                                ?.completedFieldTeamReporting
                            }
                          </h2>
                          <p className="activitySnapshotText">
                            Field Team Reports - Completed
                          </p>
                        </div>
                        <div className="activitySnapshotImg1">
                          <img src={location1} alt="img"></img>
                        </div>
                      </div>{" "}
                      {/* <div className="donorsCard">
                  {" "}
                  <div className="activitySnapshotNumber">
                    <h2 className="activitySnapshotNumber1">
                      {
                        projectData?.data?.ipDonorReporting
                          ?.awaitingFieldReportings
                      }
                    </h2>
                    <p className="activitySnapshotText">
                      IP/Donor reports - Awaiting Field
                    </p>
                  </div>
                  <div className="activitySnapshotImg1">
                    <img src={location2} alt="img"></img>
                  </div>
                </div>{" "} */}
                      <div className="donorsCard">
                        {" "}
                        <div className="activitySnapshotNumber">
                          <h2 className="activitySnapshotNumber1">
                            {
                              projectData?.data?.ipDonorReporting
                                ?.otherStatusesFieldTeamReporting
                            }
                          </h2>
                          <p className="activitySnapshotText">
                            Field Team Reports - Other Statuses
                          </p>
                        </div>
                        <div className="activitySnapshotImg1">
                          <img src={location5} alt="img"></img>
                        </div>
                      </div>{" "}
                      <div className="donorsCard">
                        {" "}
                        <div className="activitySnapshotNumber">
                          <h2 className="activitySnapshotNumber1">
                            {
                              projectData?.data?.ipDonorReporting
                                ?.completedIPDonorReports
                            }
                          </h2>
                          <p className="activitySnapshotText">
                            IP/Donor Reports - Completed
                          </p>
                        </div>
                        <div className="activitySnapshotImg1">
                          <img src={location6} alt="img"></img>
                        </div>
                      </div>{" "}
                    </div>
                  </div>
                  <div className="donorsRight">
                    {" "}
                    <div className="donorListLeft">
                      <h1 className="donorListHeaderText">IP/Donor Reports</h1>
                    </div>
                    <div className="donorListRight">
                      <h2 className="donorListHeader2Text">Last 3 Uploaded</h2>
                    </div>
                    <ul>
                      {" "}
                      {/* Assuming this is a list of reports */}
                      {projectData?.data?.ipDonorReporting?.ipDonorReports?.map(
                        (report, index) => (
                          <li key={index} className="donorListItem">
                            IP/Donor Report - {report.ipName} -{" "}
                            {report.location} - Report {report.reportNumber}
                          </li>
                        )
                      )}
                    </ul>
                  </div>
                </div>
              )}
            </div>
            {/* <div className="activitySnapshot">
              <h2 className="activitySnapshotHeader">Tasks Overview</h2>
              <div className="donorsDiv">
                {" "}
                {showGraphs && (
                  <div className="tpmSummaryCollectionGoal">
                    <div className="collectionGoalHeader">
                      <img src={iconCol1} alt="img" /> Tasks
                    </div>
                    <div className="collectionGoalProgress">
                      <center>
                        {loading ? (
                          <div>Loading...</div>
                        ) : createPieData(tasksData, siteVisitsColors) ? (
                          <div style={{ maxWidth: "400px", width: "100%" }}>
                            <TpmPie
                              data={createPieData(tasksData, siteVisitsColors)}
                              options={pieOptions}
                            />
                          </div>
                        ) : (
                          <Empty description="No data available" />
                        )}
                      </center>
                    </div>
                  </div>
                )}
                <div className="taskRight">
                  <div className="activityTaskCard">
                    <div className="activitySnapshotNumber">
                      <h2 className="activitySnapshotNumber1">
                        {
                          projectData?.data?.tasksOverviewResponse
                            ?.assignedTasks
                        }
                      </h2>
                      <p className="activitySnapshotText">Assigned Tasks</p>
                    </div>
                    <div className="activitySnapshotImg">
                      <img src={snapshot1} alt="img"></img>
                    </div>
                  </div>
                  <div className="activityTaskCard">
                    <div className="activitySnapshotNumber">
                      <h2 className="activitySnapshotNumber1">
                        {
                          projectData?.data?.tasksOverviewResponse
                            ?.acceptedTasks
                        }
                      </h2>
                      <p className="activitySnapshotText">Accepted Tasks</p>
                    </div>
                    <div className="activitySnapshotImg">
                      <img src={snapshot1} alt="img"></img>
                    </div>
                  </div>
                  <div className="activityTaskCard">
                    <div className="activitySnapshotNumber">
                      <h2 className="activitySnapshotNumber1">
                        {projectData?.data?.tasksOverviewResponse?.onHoldTasks}
                      </h2>
                      <p className="activitySnapshotText">On Hold Tasks</p>
                    </div>
                    <div className="activitySnapshotImg">
                      <img src={snapshot1} alt="img"></img>
                    </div>
                  </div>
                  <div className="activityTaskCard">
                    <div className="activitySnapshotNumber">
                      <h2 className="activitySnapshotNumber1">
                        {
                          projectData?.data?.tasksOverviewResponse
                            ?.archivedTasks
                        }
                      </h2>
                      <p className="activitySnapshotText">Archived Tasks</p>
                    </div>
                    <div className="activitySnapshotImg">
                      <img src={snapshot3} alt="img"></img>
                    </div>
                  </div>
                  <div className="activityTaskCard">
                    <div className="activitySnapshotNumber">
                      <h2 className="activitySnapshotNumber1">
                        {
                          projectData?.data?.tasksOverviewResponse
                            ?.completedTasks
                        }
                      </h2>
                      <p className="activitySnapshotText">Completed Tasks</p>
                    </div>
                    <div className="activitySnapshotImg">
                      <img src={snapshot5} alt="img"></img>
                    </div>
                  </div>
                </div>
              </div>
            </div> */}
          </>
        )}
      </div>
    </Spin>
  );
}

export default TpmSummary;
